/**
 * Metric-matched local fallbacks for the Google Fonts families in GOOGLE_FONT_PARAMS — the same
 * technique next/font uses. Theme fonts load with `display=swap`, so text first paints in the
 * generic fallback (Arial/Times/Roboto) and re-flows when the web font arrives; with a different
 * glyph width and line height that re-flow moves whole paragraphs (PageSpeed attributed shifts to
 * fonts.gstatic.com on paputypupila/glogowskiwieszak). A `'<Name> Fallback'` face is a local font
 * scaled (size-adjust) and given the web font's ascent/descent/line gap, so the swap barely moves.
 *
 * Values: [fallback kind, then for regular (400) and bold (700) text each: size-adjust %,
 * ascent-override %, descent-override %, line-gap-override %]. The overrides come from Next's capsize
 * metrics (next/dist/server/capsize-font-metrics.json: metric / (unitsPerEm × sizeAdjust)); the
 * size-adjusts were measured in Chrome as width(web font) / width(Arial or Times) of a Polish
 * sample sentence, per weight. capsize's xWidthAvg is weighted by English letter frequencies and
 * came out 2–4% too wide for Polish text, and a single regular face rendered bold text as Arial
 * with synthetic bold — up to ~9% wider than the web font's bold (Caveat, Work Sans, Space
 * Grotesk), enough to wrap a bold heading onto an extra line until the swap (glogowskiwieszak
 * hero: CLS 0.29 on mobile). Bold text now gets its own face on Arial Bold / Times Bold.
 * Regenerate when adding a family to GOOGLE_FONT_PARAMS (a family missing here simply gets no
 * fallback face — the stack still works).
 */
type FallbackMetrics = readonly ['sans' | 'serif', number, number, number, number, number, number, number, number];

const METRICS: Record<string, FallbackMetrics> = {
  'Bebas Neue': ['sans', 74.37, 121.03, 40.35, 0, 70.5, 127.67, 42.56, 0],
  // Caveat (handwriting, uneven glyph widths) 4–7% under the sample-sentence value: at the measured
  // 71.78% a glogowskiwieszak hero subtitle that fills 98.7% of its line in Caveat wrapped onto a
  // second line in the fallback until the font arrived — the whole hero moved (mobile CLS up to 0.3).
  // At ≤68% no Caveat text on glogowskiwieszak/paputypupila changes line count on the swap.
  'Caveat': ['sans', 69.94, 137.26, 42.9, 0, 67, 143.28, 44.77, 0],
  'Cinzel': ['serif', 125.42, 77.81, 29.66, 0, 121.78, 80.14, 30.55, 0],
  'Lora': ['serif', 111.35, 90.35, 24.6, 0, 108.53, 92.7, 25.24, 0],
  'Montserrat': ['sans', 109.2, 88.64, 22.99, 0, 108.21, 89.45, 23.2, 0],
  'Open Sans': ['sans', 102.58, 104.2, 28.56, 0, 102.79, 103.98, 28.5, 0],
  'Orbitron': ['sans', 120.02, 84.24, 20.25, 0, 114.87, 88.01, 21.16, 0],
  'Playfair Display': ['serif', 107.47, 100.68, 23.36, 0, 105.04, 103.01, 23.9, 0],
  'Ubuntu': ['sans', 99.47, 93.7, 19, 2.81, 98.02, 95.08, 19.28, 2.85],
  'Chakra Petch': ['sans', 98.45, 100.76, 31.29, 0, 97.81, 101.42, 31.49, 0],
  'Rajdhani': ['sans', 88.76, 104.78, 38.98, 0, 86.21, 107.88, 40.13, 0],
  'Cormorant': ['serif', 94.97, 97.3, 30.22, 0, 91.55, 100.94, 31.35, 0],
  'Cormorant Garamond': ['serif', 95.08, 97.17, 30.18, 0, 91.58, 100.89, 31.33, 0],
  'Lato': ['sans', 96.23, 102.56, 22.13, 0, 92.96, 106.17, 22.91, 0],
  'Oswald': ['sans', 81.16, 147, 35.61, 0, 85.32, 139.83, 33.87, 0],
  'Abril Fatface': ['sans', 101.11, 104.64, 28.78, 0, 95.79, 110.45, 30.38, 0],
  'DM Sans': ['sans', 100.57, 98.64, 30.83, 0, 100.14, 99.06, 30.96, 0],
  'Bodoni Moda': ['serif', 110.39, 101.92, 36.24, 0, 110.97, 101.38, 36.05, 0],
  'Plus Jakarta Sans': ['sans', 101.72, 102.05, 21.83, 0, 99.71, 104.11, 22.27, 0],
  'Tenor Sans': ['sans', 105.79, 86.97, 23.64, 0, 100.22, 91.8, 24.95, 0],
  'Mulish': ['sans', 102.66, 97.9, 24.35, 0, 100.03, 100.47, 24.99, 0],
  'Space Grotesk': ['sans', 104.57, 94.1, 27.92, 0, 99.54, 98.86, 29.33, 0],
  'Outfit': ['sans', 96.75, 103.36, 26.88, 0, 94.52, 105.8, 27.51, 0],
  'Rubik': ['sans', 101.39, 92.21, 24.65, 0, 101.73, 91.91, 24.57, 0],
  'Nunito Sans': ['sans', 99.78, 101.32, 35.38, 0, 97.44, 103.75, 36.23, 0],
  'Quicksand': ['sans', 101.42, 98.6, 24.65, 0, 99.85, 100.15, 25.04, 0],
  'Work Sans': ['sans', 107.37, 86.62, 22.63, 0, 102.35, 90.87, 23.74, 0],
  'Syne': ['sans', 95.78, 96.57, 28.71, 0, 111.8, 82.73, 24.6, 0],
  'Baloo 2': ['sans', 93.18, 115.69, 56.24, 0, 92.47, 116.58, 56.67, 0],
  'Raleway': ['sans', 101.48, 92.63, 23.06, 0, 98.99, 94.96, 23.64, 0],
  // capsize only (not measured in Chrome): 81.43% less the ~3% capsize overshoots on Polish text.
  'Dancing Script': ['sans', 79, 116.46, 35.44, 0, 75.4, 122.02, 37.14, 0],
};

// Arimo / Liberation are metric-compatible with Arial / Times New Roman (Linux, incl. PageSpeed's
// test machines). Android has none of these — there the face has no source and the browser just
// moves on to the next family in the stack, i.e. exactly the previous behaviour.
const LOCAL_SOURCES = {
  sans: "local('Arial'), local('ArialMT'), local('Arimo'), local('Liberation Sans'), local('Helvetica')",
  serif: "local('Times New Roman'), local('TimesNewRomanPSMT'), local('Tinos'), local('Liberation Serif'), local('Times')",
} as const;
const LOCAL_BOLD_SOURCES = {
  sans: "local('Arial Bold'), local('Arial-BoldMT'), local('Arimo Bold'), local('Liberation Sans Bold'), local('Helvetica Bold')",
  serif: "local('Times New Roman Bold'), local('TimesNewRomanPS-BoldMT'), local('Tinos Bold'), local('Liberation Serif Bold'), local('Times Bold')",
} as const;

const fallbackName = (family: string) => `${family} Fallback`;

/**
 * "'Rubik', sans-serif" → "'Rubik', 'Rubik Fallback', sans-serif" for families with metrics;
 * anything else (inherit, var(...), self-hosted fonts, already-expanded stacks) is returned as is.
 */
export function withFontFallback<T extends string | undefined | null>(value: T): T {
  if (typeof value !== 'string') return value;
  const match = value.match(/^\s*'([^']+)'\s*(,|$)/);
  if (!match || !METRICS[match[1]] || value.includes(`'${fallbackName(match[1])}'`)) return value;
  const family = match[1];
  const rest = value.slice(match[0].length).trim();
  return `'${family}', '${fallbackName(family)}'${rest ? `, ${rest}` : ''}` as T;
}

/** @font-face rules for every fallback face; unused faces cost nothing (local, never fetched). */
export function buildFontFallbackCss(): string {
  return Object.entries(METRICS)
    .map(([family, [kind, sizeAdjust, ascent, descent, lineGap, boldSizeAdjust, boldAscent, boldDescent, boldLineGap]]) =>
      // Regular face covers 100–500 and the bold one 600–900, so CSS font matching picks the
      // bold face (a real bold local font) for semibold/bold text instead of synthesizing it.
      `@font-face{font-family:'${fallbackName(family)}';src:${LOCAL_SOURCES[kind]};font-weight:100 500;font-display:swap;size-adjust:${sizeAdjust}%;ascent-override:${ascent}%;descent-override:${descent}%;line-gap-override:${lineGap}%}`
      + `@font-face{font-family:'${fallbackName(family)}';src:${LOCAL_BOLD_SOURCES[kind]};font-weight:600 900;font-display:swap;size-adjust:${boldSizeAdjust}%;ascent-override:${boldAscent}%;descent-override:${boldDescent}%;line-gap-override:${boldLineGap}%}`)
    .join('');
}
